import { MMKV } from "react-native-mmkv";

const mmkv = new MMKV();

// ---------------------------------------------------------------------------
// Sync API — preferred for new code (no await needed)
// ---------------------------------------------------------------------------

export const get = (key: string): string | null =>
  mmkv.getString(key) ?? null;

export const set = (key: string, value: string): void =>
  mmkv.set(key, value);

export const remove = (key: string): void => mmkv.delete(key);

export const contains = (key: string): boolean => mmkv.contains(key);

// ---------------------------------------------------------------------------
// Async API — drop-in replacement for AsyncStorage.getItem / setItem / removeItem
// ---------------------------------------------------------------------------

export const getItem = (key: string): Promise<string | null> =>
  Promise.resolve(mmkv.getString(key) ?? null);

export const setItem = (key: string, value: string): Promise<void> => {
  mmkv.set(key, value);
  return Promise.resolve();
};

export const removeItem = (key: string): Promise<void> => {
  mmkv.delete(key);
  return Promise.resolve();
};

// ---------------------------------------------------------------------------
// redux-persist storage engine (drop-in for `storage: AsyncStorage`)
// ---------------------------------------------------------------------------

export const persistStorage = { getItem, setItem, removeItem };

// ---------------------------------------------------------------------------
// Legacy lib/storage.js API — preserves existing callers that use setData /
// getData / removeData / getAppListValue with the JSON-wrapped `{ value }` shape
// ---------------------------------------------------------------------------

export const setData = (key: string, value: unknown): Promise<void> => {
  mmkv.set(key, JSON.stringify({ value }));
  return Promise.resolve();
};

export const getData = (key: string): Promise<unknown> => {
  try {
    const raw = mmkv.getString(key);
    return Promise.resolve(raw ? (JSON.parse(raw) as { value: unknown }).value : undefined);
  } catch {
    return Promise.resolve(undefined);
  }
};

export const removeData = (key: string): Promise<void> => {
  mmkv.delete(key);
  return Promise.resolve();
};

export const getAppListValue = (): Promise<string | null> =>
  Promise.resolve(mmkv.getString("getAppList") ?? null);
